import React, { useState } from 'react';
import { Flame } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import ToolPageLayout from '../../components/ToolPageLayout';
import { useLanguage } from '../../contexts/LanguageContext';

const ACTIVITY_FACTORS = [
  { value: '1.2', tKey: 'calorie_activity_sedentary' },
  { value: '1.375', tKey: 'calorie_activity_light' },
  { value: '1.55', tKey: 'calorie_activity_moderate' },
  { value: '1.725', tKey: 'calorie_activity_high' },
  { value: '1.9', tKey: 'calorie_activity_extreme' },
];

const CalorieCalculator = () => {
  const { t } = useLanguage();
  const [sex, setSex] = useState('');
  const [age, setAge] = useState('');
  const [weight, setWeight] = useState('');
  const [height, setHeight] = useState('');
  const [activity, setActivity] = useState('1.2');

  const a = parseFloat(age);
  const w = parseFloat(weight);
  const h = parseFloat(height);
  const valid = sex && a > 0 && w > 0 && h > 0;

  let bmr = null;
  if (valid) {
    bmr = sex === 'homme'
      ? 10 * w + 6.25 * h - 5 * a + 5
      : 10 * w + 6.25 * h - 5 * a - 161;
  }
  const tdee = bmr ? Math.round(bmr * parseFloat(activity)) : null;

  return (
    <ToolPageLayout
      title={t('calorie_title')}
      subtitle={t('calorie_subtitle')}
      specialty="generaliste"
    >
      <div className="grid sm:grid-cols-2 gap-5 mb-6">
        <div>
          <Label className="mb-1 block">{t('cvrisk_sex_label')}</Label>
          <select
            value={sex}
            onChange={(e) => setSex(e.target.value)}
            className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
          >
            <option value="">{t('cvrisk_sex_placeholder')}</option>
            <option value="homme">{t('cvrisk_sex_male')}</option>
            <option value="femme">{t('cvrisk_sex_female')}</option>
          </select>
        </div>
        <div>
          <Label className="mb-1 block">{t('cvrisk_age_label')}</Label>
          <Input type="number" min="10" max="100" value={age} onChange={(e) => setAge(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('bmi_weight_label')}</Label>
          <Input type="number" min="20" value={weight} onChange={(e) => setWeight(e.target.value)} className="rounded-xl" />
        </div>
        <div>
          <Label className="mb-1 block">{t('bmi_height_label')}</Label>
          <Input type="number" min="100" value={height} onChange={(e) => setHeight(e.target.value)} className="rounded-xl" />
        </div>
      </div>

      <div className="mb-6">
        <Label className="mb-1 block">{t('calorie_activity_label')}</Label>
        <select
          value={activity}
          onChange={(e) => setActivity(e.target.value)}
          className="w-full border border-[#E2E8F0] rounded-xl px-3 py-2 text-sm bg-white h-10 focus:outline-none focus:ring-2 focus:ring-[#0596DE]"
        >
          {ACTIVITY_FACTORS.map((f) => (
            <option key={f.value} value={f.value}>{t(f.tKey)}</option>
          ))}
        </select>
      </div>

      {tdee && (
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5 text-center">
            <p className="text-xs text-[#64748B] mb-1">{t('calorie_weight_loss')}</p>
            <p className="text-2xl font-bold text-[#0F172A]">{tdee - 500}</p>
            <p className="text-xs text-[#64748B]">{t('calorie_per_day')}</p>
          </div>
          <div className="bg-[#E6F4FB] border border-[#0596DE]/30 rounded-2xl p-5 text-center">
            <p className="text-xs text-[#0596DE] mb-1 font-semibold">{t('calorie_maintenance')}</p>
            <p className="text-2xl font-bold text-[#0596DE]">{tdee}</p>
            <p className="text-xs text-[#0596DE]">{t('calorie_per_day')}</p>
          </div>
          <div className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-2xl p-5 text-center">
            <p className="text-xs text-[#64748B] mb-1">{t('calorie_weight_gain')}</p>
            <p className="text-2xl font-bold text-[#0F172A]">{tdee + 500}</p>
            <p className="text-xs text-[#64748B]">{t('calorie_per_day')}</p>
          </div>
        </div>
      )}

      <p className="mt-6 text-sm text-[#64748B]">
        <Flame className="w-4 h-4 inline text-[#F59E0B] mr-1" />
        {t('calorie_disclaimer')}
      </p>
    </ToolPageLayout>
  );
};

export default CalorieCalculator;
